<template>
  <div class="audio-recorder-inline">
    <!-- Idle / processing state: mic button -->
    <v-btn
      v-if="!isRecording"
      icon
      size="small"
      variant="text"
      :disabled="isProcessing"
      class="mic-btn"
      title="Record voice message"
      @click="startRecording"
    >
      <v-progress-circular
        v-if="isSending"
        indeterminate
        size="18"
        width="2"
        color="primary"
      />
      <i
        v-else
        class="pi pi-microphone"
      />
    </v-btn>

    <!-- Recording state: compact inline bar -->
    <div
      v-else
      class="recording-inline"
    >
      <i
        class="pi pi-circle-fill pulse-icon"
        style="color: red; font-size: 8px;"
      />
      <span class="recording-timer">{{ elapsedTime }}</span>
      <v-btn
        icon
        size="x-small"
        variant="text"
        title="Cancel recording"
        class="recording-action-btn"
        @click="cancelRecording"
      >
        <i
          class="pi pi-times"
          style="font-size: 18px;"
        />
      </v-btn>
      <v-btn
        icon
        size="x-small"
        color="error"
        variant="text"
        title="Stop and send"
        class="recording-action-btn"
        @click="stopRecording"
      >
        <i
          class="pi pi-stop"
          style="font-size: 18px;"
        />
      </v-btn>
    </div>

    <!-- Error display -->
    <div
      v-if="errorMessage"
      class="recorder-error"
    >
      <span>{{ errorMessage }}</span>
      <v-btn
        icon
        size="x-small"
        variant="text"
        @click="clearError"
      >
        <i
          class="pi pi-times"
          style="font-size: 14px;"
        />
      </v-btn>
    </div>
  </div>
</template>

<script>
import audioService from '@/services/audioService'
import {
  AUDIO_MAX_DURATION_MS,
  AUDIO_RECORDING_START_ERROR,
  AUDIO_RECORDING_NOT_SUPPORTED,
  AUDIO_EMPTY_RECORDING
} from '@/assets/constants/constants'

export default {
  name: 'AudioRecorder',

  emits: ['audio-recorded', 'recording-started', 'recording-stopped', 'recording-cancelled'],

  data() {
    return {
      isRecording: false,
      isProcessing: false,
      isSending: false,
      recordingBlob: null,
      recordingDuration: 0,
      elapsedSeconds: 0,
      errorMessage: '',

      // Internal state
      mediaStream: null,
      mediaRecorder: null,
      recordingStartTime: null,
      recordingTimer: null,
      recordedChunks: []
    }
  },

  computed: {
    elapsedTime() {
      return audioService.formatDuration(this.elapsedSeconds * 1000)
    }
  },

  watch: {
    elapsedSeconds(newVal) {
      // Auto-stop at 30 seconds
      if (newVal >= 30) {
        this.stopRecording()
      }
    }
  },

  beforeUnmount() {
    this.cleanup()
  },

  methods: {
    async startRecording() {
      this.clearError()

      // Check browser support
      if (!audioService.isRecordingSupported()) {
        this.setError(AUDIO_RECORDING_NOT_SUPPORTED)
        return
      }

      try {
        this.isProcessing = true

        // Request microphone access
        this.mediaStream = await audioService.requestMicrophoneAccess()

        // Create and configure recorder
        this.mediaRecorder = audioService.createRecorder(this.mediaStream)
        this.recordedChunks = []

        // Collect audio chunks
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.recordedChunks.push(event.data)
          }
        }

        this.mediaRecorder.onerror = (event) => {
          this.setError(`Recording error: ${event.error}`)
          this.isRecording = false
        }

        // Start recording
        this.mediaRecorder.start()
        this.isRecording = true
        this.recordingStartTime = Date.now()
        this.elapsedSeconds = 0

        // Start timer
        this.recordingTimer = setInterval(() => {
          this.elapsedSeconds = Math.floor(
            (Date.now() - this.recordingStartTime) / 1000
          )
        }, 100)

        this.$emit('recording-started')
      } catch (error) {
        this.setError(error.message || AUDIO_RECORDING_START_ERROR)
      } finally {
        this.isProcessing = false
      }
    },

    stopRecording() {
      if (!this.isRecording || !this.mediaRecorder) return

      this.isRecording = false
      this.clearTimer()
      this.isSending = true
      // Restore parent input immediately; spinner shows on mic button during processing
      this.$emit('recording-stopped')

      this.mediaRecorder.onstop = async () => {
        await this.createRecordingBlob()
        this.isSending = false
      }

      this.mediaRecorder.stop()
      audioService.stopMediaStream(this.mediaStream)
    },

    cancelRecording() {
      if (!this.isRecording) return

      this.isRecording = false
      this.clearTimer()

      // Stop recording and discard chunks (no abort() on MediaRecorder)
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.ondataavailable = null
        this.mediaRecorder.onstop = null
        this.mediaRecorder.stop()
      }

      audioService.stopMediaStream(this.mediaStream)
      this.recordedChunks = []
      this.recordingBlob = null
      this.recordingDuration = 0
      this.elapsedSeconds = 0

      this.$emit('recording-cancelled')
    },

    async createRecordingBlob() {
      if (this.recordedChunks.length === 0) {
        this.setError(AUDIO_EMPTY_RECORDING)
        return
      }

      // Create blob from chunks
      const mimeType = this.mediaRecorder.mimeType
      this.recordingBlob = new Blob(this.recordedChunks, { type: mimeType })

      // Calculate actual duration
      try {
        this.recordingDuration = await audioService.getAudioDuration(
          this.recordingBlob
        )
      } catch (error) {
        // Fallback to elapsed time if duration detection fails
        this.recordingDuration = this.elapsedSeconds * 1000
      }

      // Auto-send immediately
      await this.sendRecording()
    },

    async sendRecording() {
      if (!this.recordingBlob) return

      try {
        // Validate duration
        const validation = audioService.validateDuration(this.recordingDuration)
        if (!validation.valid) {
          this.setError(validation.error)
          return
        }

        // Validate blob
        const blobValidation = audioService.validateAudioBlob(this.recordingBlob)
        if (!blobValidation.valid) {
          this.setError(blobValidation.error)
          return
        }

        // Convert to base64
        const base64Audio = await audioService.blobToBase64(this.recordingBlob)

        // Emit audio data for parent component to send via WebSocket
        this.$emit('audio-recorded', {
          audioData: base64Audio,
          durationMs: this.recordingDuration,
          blob: this.recordingBlob
        })

        // Reset state after sending
        this.recordingBlob = null
        this.recordingDuration = 0
        this.elapsedSeconds = 0
        this.recordedChunks = []
      } catch (error) {
        this.setError(error.message || 'Failed to process recording')
      }
    },

    setError(message) {
      this.errorMessage = message
    },

    clearError() {
      this.errorMessage = ''
    },

    clearTimer() {
      if (this.recordingTimer) {
        clearInterval(this.recordingTimer)
        this.recordingTimer = null
      }
    },

    cleanup() {
      this.clearTimer()

      if (this.isRecording) {
        this.cancelRecording()
      }

      if (this.mediaStream) {
        audioService.stopMediaStream(this.mediaStream)
      }
    }
  }
}
</script>

<style scoped>
.audio-recorder-inline {
  display: flex;
  align-items: center;
}

.mic-btn {
  width: 44px !important;
  height: 44px !important;
  flex-shrink: 0;
  color: var(--color-aurora-start) !important;
  border: 1.5px solid transparent !important;
  border-radius: 50% !important;
  background-image:
    linear-gradient(var(--color-surface), var(--color-surface)),
    var(--gradient-aurora) !important;
  background-origin: border-box !important;
  background-clip: padding-box, border-box !important;
  transition: opacity var(--transition-fast);
}

.mic-btn:hover:not(:disabled) {
  opacity: 0.8;
}

.recording-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  border: 1.5px solid transparent;
  border-radius: 24px;
  padding: 8px 14px;
  background-image:
    linear-gradient(rgba(254, 242, 242, 0.85), rgba(254, 242, 242, 0.85)),
    var(--gradient-aurora);
  background-origin: border-box;
  background-clip: padding-box, border-box;
}

.pulse-icon {
  animation: pulse 1s infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.recording-timer {
  font-size: 0.875rem;
  font-weight: 600;
  color: #dc2626;
  font-variant-numeric: tabular-nums;
  flex: 1;
}

.recording-action-btn {
  flex-shrink: 0;
  color: #6b7280;
}

.recorder-error {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #dc2626;
  padding: 0 4px;
  flex-shrink: 0;
}
</style>
